import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { NavigationProps } from "../utils/navigationProps";
import { RootState } from "../store/rootReducer";
import { CardDownloader } from "../cards/downloader/cardDownloader";
import { markupFormatter } from "../common";
import { ISubcard } from "../cards/multicards.interface";
import { cardQuestionText } from "../utils/cardQuestionText";
import { usePageTitle } from "../hooks/usePageTitle";
import { NotFound404 } from "../components/NotFound404";
import { SubcardView } from "../review/subcardView/subcardView";
import { ReviewState } from "../review/review.interface";
import { CardMarkupViewerState } from "../markup/cardMarkupViewer/cardMarkupViewer.interface";
import { getNextSubcardIndex } from "../review/utils/multicard.utils";
import { api } from "../api/api";
import { ThemeProps } from "../styles/theme";
import { Content, Message, Page, PageTitle, PrimaryButton } from "../train/trainUi";

type Props = NavigationProps & {
    cardId?: string;
};

const Card = styled.div<ThemeProps>`
    width: 100%;
    display: flex;
    flex-direction: column;
    background-color: ${props => props.theme.primaryBackground};
    border: ${props => props.theme.primaryBorder} 2px solid;
    box-shadow: 4px 4px ${props => props.theme.cardShadow};
    padding: 20px;
    box-sizing: border-box;
`;

const Buttons = styled.div`
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-top: 20px;
`;

const ResultButton = styled.button`
    padding: 8px 20px;
    border-radius: 4px;
    cursor: pointer;
    color: #ffffff;
    font-family: inherit;

    &:hover {
        opacity: 0.9;
    }
`;

const WrongButton = styled(ResultButton)<ThemeProps>`
    background-color: ${props => props.theme.dangerColor || '#dc3545'};
    border: 1px solid ${props => props.theme.dangerColor || '#dc3545'};
`;

const RightButton = styled(ResultButton)<ThemeProps>`
    background-color: ${props => props.theme.successColor || '#28a745'};
    border: 1px solid ${props => props.theme.successColor || '#28a745'};
`;

const ShowAnswer = styled(PrimaryButton)`
    align-self: center;
`;

const Progress = styled.div<ThemeProps>`
    font-size: 13px;
    color: ${props => props.theme.secondaryText};
    margin-bottom: 16px;
    text-align: center;
`;

const SummaryScore = styled.div<ThemeProps>`
    font-size: 32px;
    font-weight: 700;
    text-align: center;
    color: ${props => props.theme.primaryText};
`;

type Phase = 'question' | 'answer';

interface Session {
    order: number[];
    correct: number;
}

const buildOrder = (subCards: ISubcard[]): number[] => {
    const order: number[] = [];
    let next = getNextSubcardIndex(subCards, order);
    while (next !== undefined) {
        order.push(next);
        next = getNextSubcardIndex(subCards, order);
    }
    return order;
};

export const PracticeSubcards: FC<Props> = (props) => {
    const cardId = props.cardId;

    const selector = useCallback(
        (state: RootState) => (cardId !== undefined ? state.cards[cardId] : undefined),
        [cardId]
    );
    const card = useSelector(selector);

    const [notFound, setNotFound] = useState(false);
    useEffect(() => {
        if (cardId === undefined || cardId === "") {
            setNotFound(true);
            return;
        }
        setNotFound(false);
        if (card !== undefined)
            return;
        const timer = setTimeout(() => {
            if (card === undefined)
                setNotFound(true);
        }, 800);
        return () => clearTimeout(timer);
    }, [cardId, card]);

    const subCards = useMemo(
        () => (card !== undefined ? markupFormatter.parse(card.markup).subCards : []),
        [card]
    );

    const question = card !== undefined ? cardQuestionText(card.markup) : "";
    usePageTitle(`Practice · ${question || "Card"} · Memkeep`);

    const [session, setSession] = useState<Session | undefined>(undefined);
    const [position, setPosition] = useState(0);
    const [phase, setPhase] = useState<Phase>('question');

    const startSession = useCallback(() => {
        setSession({ order: buildOrder(subCards), correct: 0 });
        setPosition(0);
        setPhase('question');
    }, [subCards]);

    useEffect(() => {
        if (session === undefined && subCards.length > 0)
            startSession();
    }, [session, subCards.length, startSession]);

    const currentSubcard = useMemo(() => {
        if (session === undefined || position >= session.order.length)
            return undefined;
        return subCards[session.order[position]];
    }, [session, position, subCards]);

    // When the current subcard appeared, for the answer time saved with the result.
    const shownAt = useRef(Date.now());
    useEffect(() => {
        shownAt.current = Date.now();
    }, [currentSubcard]);

    const markResult = useCallback((isCorrect: boolean) => {
        if (currentSubcard === undefined || cardId === undefined)
            return;
        const timeSpent = Date.now() - shownAt.current;
        void api.practiceSubcard({ cardId, subcard: currentSubcard, isCorrect, timeSpent }).catch(() => undefined);
        setSession(prev => prev !== undefined
            ? { ...prev, correct: prev.correct + (isCorrect ? 1 : 0) }
            : prev);
        setPosition(prev => prev + 1);
        setPhase('question');
    }, [currentSubcard, cardId]);

    if (notFound)
        return <NotFound404 type="card" id={cardId} />;

    const isFinished = session !== undefined && position >= session.order.length;

    return (
        <Page>
            <CardDownloader cardId={cardId} />
            <Content>
                <PageTitle>Practice · {question}</PageTitle>

                {card === undefined ? (
                    <Message>Loading…</Message>
                ) : subCards.length === 0 ? (
                    <Message>This card has no subcards to practice.</Message>
                ) : isFinished && session !== undefined ? (
                    <>
                        <SummaryScore>{session.correct} / {session.order.length}</SummaryScore>
                        <Message>Practice complete. You can close this tab.</Message>
                        <PrimaryButton onClick={startSession}>Practice again</PrimaryButton>
                    </>
                ) : session !== undefined && currentSubcard !== undefined ? (
                    <>
                        <Progress>{position + 1} / {session.order.length}</Progress>
                        <Card>
                            <SubcardView
                                cardId={cardId}
                                currentSubcard={currentSubcard}
                                reviewState={phase === 'question' ? ReviewState.SubcardQuestion : ReviewState.SubcardAnswer}
                                markupState={phase === 'question' ? CardMarkupViewerState.OnlyQuestion : CardMarkupViewerState.Full}
                                cardMarkup={card.markup}
                            />
                        </Card>
                        {phase === 'question' ? (
                            <Buttons>
                                <ShowAnswer onClick={() => setPhase('answer')}>Show Answer</ShowAnswer>
                            </Buttons>
                        ) : (
                            <Buttons>
                                <WrongButton onClick={() => markResult(false)}>Got It Wrong</WrongButton>
                                <RightButton onClick={() => markResult(true)}>Got It Right</RightButton>
                            </Buttons>
                        )}
                    </>
                ) : (
                    <Message>Loading…</Message>
                )}
            </Content>
        </Page>
    );
};
PracticeSubcards.displayName = 'PracticeSubcards';
